import { useState } from 'react';
import { motion } from 'framer-motion';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface TenantFaqSectionProps {
  title?: string;
  description?: string;
}

const INITIAL_COUNT = 5;

export function TenantFaqSection({
  title = 'Frequently Asked Questions',
  description = 'Find answers to common questions about our community.',
}: TenantFaqSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const { data, error, isLoading } = useSWR<{ faqs: FAQItem[] }>('/api/tenants/faqs', fetcher);

  if (error) {
    console.error('Failed to load FAQs:', error);
    return null;
  }

  if (isLoading) {
    return (
      <section className="py-24 bg-muted/30 scroll-mt-24">
        <div className="container px-4 sm:px-6 mx-auto max-w-3xl">
          <div className="text-center mb-12 space-y-4">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="rounded-2xl border border-border/50 bg-background p-4 sm:p-6 space-y-4 shadow-sm">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </section>
    );
  }

  const faqs = data?.faqs || [];

  if (faqs.length === 0) {
    return null;
  }

  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_COUNT);
  const hasMore = !showAll && faqs.length > INITIAL_COUNT;

  return (
    <section id="faqs" className="py-24 bg-muted/30 scroll-mt-24">
      <div className="container px-4 sm:px-6 mx-auto max-w-3xl">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">{title}</h2>
          <p className="text-muted-foreground text-lg font-normal">{description}</p>
        </div>

        <div className="space-y-3">
          {visibleFaqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              whileHover={{ y: -4, boxShadow: '0 12px 32px -4px rgba(0, 0, 0, 0.15)' }}
              className="rounded-xl border border-neutral-200 bg-white px-4 sm:px-6 shadow-sm"
            >
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value={`faq-${index}`} className="border-none">
                  <AccordionTrigger className="text-left text-base font-semibold py-4 text-neutral-900 hover:text-primary transition-colors hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground leading-relaxed pb-4 whitespace-pre-line">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          ))}
        </div>

        {hasMore && (
          <div className="mt-5 text-end">
            <button
              onClick={() => setShowAll(true)}
              className="text-sm font-medium text-primary border border-primary/30 rounded-[11px] px-5 py-1.5 bg-white hover:bg-primary/5 transition-colors"
            >
              More
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
